import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import colorSchemes from './colorSchemes';

// Extend the Theme interface to include our custom properties
declare module '@mui/material/styles' {
  interface Palette {
    color1?: string;
    color2?: string;
    customMain?: string;
    border?: string;
    DataGrid?: {
      bg: string;
      pinnedBg: string;
      headerBg: string;
    };
  }
}

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // Check localStorage for saved theme preference
    const savedMode = localStorage.getItem('themeMode') as ThemeMode;
    return savedMode || 'light';
  });

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Create MUI theme with our color schemes
  const theme = createTheme({
    palette: {
      mode,
      ...colorSchemes[mode].palette,
    },
    components: {
      // Override MUI component styles to use our custom colors
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colorSchemes[mode].palette.background.default,
            color: colorSchemes[mode].palette.text.primary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: colorSchemes[mode].palette.background.paper,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colorSchemes[mode].palette.background.paper,
            color: colorSchemes[mode].palette.text.primary,
          },
        },
      },
    },
  });

  const contextValue = useMemo<ThemeContextType>(()=> ({
    mode,
    toggleMode,
    theme,
  }), [
    mode,
    toggleMode,
    theme,
  ]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;